import { type FC, useState } from 'react';
import { Button } from '@components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from '@components/ui/empty';
import { Kbd } from '@components/ui/kbd';
import { Label } from '@components/ui/label';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from 'cn';
import { CircleX, SearchIcon, XIcon } from 'lucide-react';

import { RecentItemsList } from '../components/RecentItemsList';
import { FILTER_OPTIONS, TYPE_LABELS } from '../constants';
import { SearchResultsController } from '../controller/SearchResultsController';
import type { SearchResultType, SearchSort } from '../types';

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
const modKey = isMac ? '⌘' : 'Ctrl';

const SORT_OPTIONS: { label: string; value: SearchSort }[] = [
  { label: 'Relevance', value: 'relevance' },
  { label: 'Recent', value: 'recent' },
];

export interface CommandPaletteViewProps {
  isOpen: boolean;
  isUserRole: boolean;
  query: string;
  debouncedQuery: string;
  type?: SearchResultType;
  sort: SearchSort;
  hasQuery: boolean;
  error?: string;
  onOpenChange: (open: boolean) => void;
  onQueryChange: (query: string) => void;
  onTypeChange: (type?: SearchResultType) => void;
  onSortChange: (sort: SearchSort) => void;
}

export const CommandPalette: FC<CommandPaletteViewProps> = ({
  isOpen,
  isUserRole,
  query,
  debouncedQuery,
  type,
  sort,
  hasQuery,
  error,
  onOpenChange,
  onQueryChange,
  onTypeChange,
  onSortChange,
}) => {
  'use memo';

  const [itemCount, setItemCount] = useState(0);
  const [totalItemCount, setTotalItemCount] = useState(0);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-24 flex max-h-[70vh] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">Search</DialogTitle>

          <div className="flex items-center gap-3 border-b px-4">
            <SearchIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls="command-palette-list"
              aria-label="Search contracts, templates, blocks and clauses"
              placeholder="Search contracts, templates, blocks and clauses…"
              className="placeholder:text-muted-foreground h-12 flex-1 bg-transparent text-sm outline-none"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
            />

            {hasQuery && (
              <Button size="xs" variant="ghost" onClick={() => onQueryChange('')}>
                Clear
              </Button>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
                  <XIcon className="size-4 shrink-0" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="z-5000">Close Search</TooltipContent>
            </Tooltip>
          </div>

          {hasQuery && !error && (
            <div className="flex items-center gap-1.5 border-b px-3 py-2">
              {!isUserRole && (
                <RadioGroup
                  value={type ?? 'all'}
                  onValueChange={(value) => {
                    if (value === 'all') {
                      onTypeChange(undefined);
                    } else {
                      onTypeChange(value as SearchResultType);
                    }
                  }}
                  orientation="horizontal"
                  className="flex w-fit flex-row gap-2"
                >
                  {[undefined, ...FILTER_OPTIONS.map((option) => option.value)].map((value) => (
                    <div key={value ?? 'all'}>
                      <RadioGroupItem value={value ?? 'all'} id={`desc-r${value ?? 'all'}`} className="peer sr-only" />
                      <Label
                        htmlFor={`desc-r${value ?? 'all'}`}
                        className={cn(
                          'cursor-pointer rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                          type === value
                            ? 'border-primary-500 bg-primary-500 text-white'
                            : 'text-muted-foreground hover:bg-accent border-transparent',
                        )}
                      >
                        {value ? TYPE_LABELS[value] : 'All'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
              <span className="flex-1" />
              <RadioGroup
                value={sort}
                onValueChange={(value) => onSortChange(value as SearchSort)}
                orientation="horizontal"
                className="flex items-center gap-0.5 rounded-full border p-0.5"
              >
                {SORT_OPTIONS.map((option) => (
                  <div key={option.value}>
                    <RadioGroupItem value={option.value} id={`sort-r${option.value}`} className="peer sr-only" />
                    <Label
                      htmlFor={`sort-r${option.value}`}
                      className={cn(
                        'cursor-pointer rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
                        sort === option.value
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}
        </DialogHeader>
        {error && (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="default" className="text-muted-foreground">
                <CircleX aria-hidden />
              </EmptyMedia>
              <EmptyDescription>{error}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {hasQuery && !error ? (
          <SearchResultsController
            query={query}
            type={type}
            sort={sort}
            isOpen={isOpen}
            debouncedQuery={debouncedQuery}
            hasQuery={hasQuery}
            setItemCount={setItemCount}
            setTotalItemCount={setTotalItemCount}
          />
        ) : (
          !error && <RecentItemsList setQuery={onQueryChange} />
        )}

        <DialogFooter className="text-muted-foreground flex items-center gap-3 border-t px-4 py-2 text-xs sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd> open
            </span>
            <span className="flex items-center gap-1">
              <Kbd>{modKey}</Kbd>
              <Kbd>↵</Kbd> open in new tab
            </span>
          </div>
          {itemCount > 0 && totalItemCount > 0 && (
            <div className="flex items-center gap-3">
              <span className="font-bold">
                {itemCount} of {totalItemCount} results
              </span>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
